import { useRef, useState } from 'react';
import { AxiosError } from 'axios';
import produce from 'immer';
import { useQueryClient } from '@tanstack/react-query';
import { HomeTab, HomeTabList, HomeTabListOption } from '../../types/home/tab';
import { homeTabConverter, useHomeTabListQuery } from '../queries/home/useHomeTabListQuery';
import { HOME_TAB_STATUS_TYPE_V2 } from '../../constants/service/app/homeConstantsV2';
import { deepEqual } from '../../lib/utils/object/deepEqual';
import { homeQueryKeys } from '../../constants/queryKeys/home';

type UseHomeTabListReturnType = {
  totalCount: number;
  homeTabList: HomeTab[];
  isLoading: boolean;
  error: AxiosError | null;
  isHomeTabListChanged: boolean;
  refetchHomeTabList: () => void;
  resetHomeTabList: () => void;
  changeHomeTabListOption: (option: HomeTabListOption) => void;
  changeHomeTabStatus: (id: number, checked: boolean) => void;
  changeHomeTabListOrder: (newList: HomeTab[]) => void;
};

export const useHomeTabList = (): UseHomeTabListReturnType => {
  const queryClient = useQueryClient();
  const originHomeTabList = useRef<HomeTabList>();

  const [homeTabListOption, setHomeTabListOption] = useState<HomeTabListOption>({ page: 1, pageSize: 20 });
  const { data, isLoading, error, refetch } = useHomeTabListQuery(homeTabListOption, {
    onSuccess: (res) => {
      originHomeTabList.current = res;
    },
    cacheTime: 0,
  });

  const changeHomeTabListQuery = (homeTabListData: HomeTabList): void => {
    queryClient.setQueryData(homeQueryKeys.tabList(homeTabListOption), {
      totalCount: homeTabListData.totalCount,
      homeTabs: homeTabListData.homeTabList.map(homeTabConverter.clientToServer),
    });
  };

  const changeHomeTabListOption = (option: HomeTabListOption): void => {
    setHomeTabListOption(option);
  };

  const changeHomeTabStatus = (id: number, checked: boolean): void => {
    if (!data) return;
    const newList = produce(data, (draft) => {
      draft.homeTabList = draft?.homeTabList.map((tab) => {
        if (tab.id === id) {
          return { ...tab, status: checked ? HOME_TAB_STATUS_TYPE_V2.ACTIVE : HOME_TAB_STATUS_TYPE_V2.INACTIVE };
        }

        return tab;
      });
    });

    if (!newList) return;
    changeHomeTabListQuery(newList);
  };

  const changeHomeTabListOrder = (newList: HomeTab[]): void => {
    changeHomeTabListQuery({
      totalCount: data?.totalCount || 0,
      homeTabList: newList.map((tab, idx) => ({ ...tab, no: idx + 1 })),
    });
  };

  const resetHomeTabList = (): void => {
    if (originHomeTabList.current) changeHomeTabListQuery(originHomeTabList.current);
  };

  return {
    totalCount: data?.totalCount || 0,
    homeTabList: [...(data?.homeTabList || [])].sort((tab1, tab2) => tab1.no - tab2.no),
    isLoading,
    error,
    isHomeTabListChanged: !deepEqual(originHomeTabList.current, data),
    refetchHomeTabList: refetch,
    resetHomeTabList,
    changeHomeTabListOption,
    changeHomeTabStatus,
    changeHomeTabListOrder,
  };
};
